import FinalCTA from "@/components/creatorshield/FinalCTA";
import Footer from "@/components/creatorshield/Footer";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import {
  BookOpen,
  Minus,
  Plus,
  Rocket,
  Search,
  Settings,
  Wallet,
  X,
} from "lucide-react";
import QuestionMark from "@/assets/svg/creator-shield/question-mark.svg";
import { useEffect, useState } from "react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

export default function CreatorshieldFAQ() {
  const [searchQuery, setSearchQuery] = useState("");
  const headingRef = useScrollAnimation({ threshold: 0.2 });

  // General questions (no category)
  const generalFAQs = [
    {
      id: "general-0",
      question: "What is CreatorShield?",
      answer: (
        <>
          <p className="mb-0">
            Did you know that over $3B in royalties are wrongfully paid out to
            content thefts each year?
          </p>
          <p className="mb-0">&nbsp;</p>
          <p className="mb-0">
            Nowadays, content from your social media pages like Facebook,
            YouTube, Instagram, and TikTok can be easily stolen and re-uploaded
            on other people's channels and accounts. What's worse is they can be
            earning using the videos that you worked hard for.
          </p>
          <p className="mb-0">&nbsp;</p>
          <p className="mb-0">
            To address this issue, we have started offering our Digital Rights
            Management service - CreatorShield. We help creators get a handle on
            their copyright management and put their money back into their
            pockets that would otherwise be paid to people re-uploading their
            content.
          </p>
          <p className="mb-0">&nbsp;</p>
          <p className="mb-0 text-black">
            Perks of signing up for this service:
          </p>
          <p className="mb-0">&nbsp;</p>
          <ul className="list-disc">
            <li className="mb-0 ms-[27px]">
              <span>Copyright protection</span>
            </li>
            <li className="mb-0 ms-[27px]">
              <span>
                Unlock a new revenue stream - all money recovered by
                CreatorShield will be on top of what you are currently earning
                right now
              </span>
            </li>
            <li className="ms-[27px]">
              <span>
                Completely free - just sign up and we'll do all the work for you
              </span>
            </li>
          </ul>
        </>
      ),
      isOrange: true,
    },
    {
      id: "general-1",
      question: "How does CreatorShield work?",
      answer:
        "CreatorShield monitors major platforms like YouTube, Facebook, Instagram, and TikTok for unauthorized reuploads of your content. When we detect stolen content, we automatically file copyright claims on your behalf. Any revenue generated from these claims goes directly to you, minus our service fee.",
    },
    {
      id: "general-2",
      question:
        "If I have a YouTube account, will it affect my channel and the revenue I'm earning from AdSense?",
      answer:
        "No, CreatorShield will not affect your existing YouTube channel or AdSense revenue. We only claim against unauthorized reuploads of your content on other channels. Your original content and earnings remain completely unaffected.",
    },
    {
      id: "general-3",
      question:
        "If I have the Copyright Match Tool on YouTube, can I still use it?",
      answer:
        "Yes, you can still use YouTube's Copyright Match Tool alongside CreatorShield. However, CreatorShield provides a more comprehensive solution by monitoring multiple platforms beyond YouTube and handling the entire copyright claim process for you automatically.",
    },
  ];

  // Before Joining section
  const beforeJoiningFAQs = [
    {
      id: "before-1",
      question: "How much can I earn from CreatorShield?",
      answer:
        "Earnings vary based on the amount of unauthorized content being reuploaded. Many creators earn hundreds to thousands of dollars per month in additional revenue. The more content you have that gets stolen, the more potential earnings you can recover.",
    },
    {
      id: "before-2",
      question: "When do I start getting paid?",
      answer:
        "After completing the signup process, we begin monitoring your content immediately. Earnings typically start appearing within 30-60 days as we identify and claim unauthorized reuploads. Payments are processed monthly once claims are resolved.",
    },
  ];

  // Getting Paid section
  const gettingPaidFAQs = [
    {
      id: "paid-1",
      question: "When does CreatorShield pay?",
      answer:
        "CreatorShield processes payments monthly. Once copyright claims are resolved and revenue is collected, we distribute your share (minus our service fee) to your account. Payment timelines can vary depending on platform processing times.",
    },
    {
      id: "paid-2",
      question: "What payment methods are available?",
      answer:
        "We support various payment methods including bank transfers, PayPal, and other digital payment platforms. You can set your preferred payment method in your account settings after signing up.",
    },
  ];

  // General Settings section
  const generalSettingsFAQs = [
    {
      id: "settings-1",
      question: "How does the Copyright Match Tool work?",
      answer:
        "The Copyright Match Tool is YouTube's built-in feature that identifies videos similar to yours. CreatorShield goes beyond this by monitoring multiple platforms and automatically filing claims. We also handle the entire process, so you don't need to manually review each match.",
    },
    {
      id: "settings-2",
      question: "How does Content ID work?",
      answer:
        "Content ID is YouTube's automated system for identifying and managing copyrighted content. CreatorShield works with Content ID by helping you set up and manage your Content ID claims more effectively, ensuring you maximize your revenue from unauthorized reuploads.",
    },
    {
      id: "settings-3",
      question: "How can I earn money on TikTok?",
      answer:
        "CreatorShield helps you earn money on TikTok by protecting your content from unauthorized reuploads. When your TikTok videos are stolen and reuploaded on other platforms, we automatically file copyright claims and recover the revenue that should have gone to you. This creates an additional income stream on top of your existing TikTok earnings.",
    },
  ];

  // Combine all FAQs for search
  const allFAQs = [
    ...generalFAQs,
    ...beforeJoiningFAQs,
    ...gettingPaidFAQs,
    ...generalSettingsFAQs,
  ];

  const [filteredGeneral, setFilteredGeneral] = useState(generalFAQs);
  const [filteredBeforeJoining, setFilteredBeforeJoining] =
    useState(beforeJoiningFAQs);
  const [filteredGettingPaid, setFilteredGettingPaid] =
    useState(gettingPaidFAQs);
  const [filteredGeneralSettings, setFilteredGeneralSettings] =
    useState(generalSettingsFAQs);

  // Helper function to extract text from React elements
  const extractTextFromReactElement = (element) => {
    if (typeof element === "string" || typeof element === "number") {
      return String(element);
    }
    if (Array.isArray(element)) {
      return element.map(extractTextFromReactElement).join(" ");
    }
    if (element && typeof element === "object" && element.props) {
      if (element.props.children) {
        return extractTextFromReactElement(element.props.children);
      }
    }
    return "";
  };

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredGeneral(generalFAQs);
      setFilteredBeforeJoining(beforeJoiningFAQs);
      setFilteredGettingPaid(gettingPaidFAQs);
      setFilteredGeneralSettings(generalSettingsFAQs);
    } else {
      const query = searchQuery.toLowerCase();
      const filterFAQs = (faqs) =>
        faqs.filter((faq) => {
          // Always check the question
          const questionMatch = faq.question.toLowerCase().includes(query);

          // Check answer - handle both string and React element
          let answerText = "";
          if (typeof faq.answer === "string") {
            answerText = faq.answer;
          } else {
            // Extract text from React element
            answerText = extractTextFromReactElement(faq.answer);
          }
          const answerMatch = answerText.toLowerCase().includes(query);

          return questionMatch || answerMatch;
        });

      setFilteredGeneral(filterFAQs(generalFAQs));
      setFilteredBeforeJoining(filterFAQs(beforeJoiningFAQs));
      setFilteredGettingPaid(filterFAQs(gettingPaidFAQs));
      setFilteredGeneralSettings(filterFAQs(generalSettingsFAQs));
    }
  }, [searchQuery]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  const [openItems, setOpenItems] = useState(["general-0"]); // Default open: "What is CreatorShield?"

  const renderFAQSection = (faqs, sectionId) => {
    if (faqs.length === 0) return null;

    return (
      <Accordion
        type="multiple"
        value={openItems}
        onValueChange={setOpenItems}
        className="flex flex-col gap-[25px]"
      >
        {faqs.map((faq) => {
          const isOpen = openItems.includes(faq.id);
          return (
            <AccordionItem
              key={faq.id}
              value={faq.id}
              className="bg-white border-[#ddd] border-[0.5px] border-solid rounded-[20px] px-[24px] py-2 overflow-clip flex flex-col gap-2 last:!border-b-[0.5px]"
            >
              <AccordionTrigger className="hover:no-underline text-left [&>svg]:hidden flex items-center justify-between w-full gap-[40px]">
                <span
                  className={`font-semibold text-xl flex-1 ${faq.isOrange ? "text-[#FF6B35]" : "text-black"}`}
                >
                  {faq.question}
                </span>
                <div className="shrink-0 w-6 h-6 flex items-center justify-center">
                  {isOpen ? (
                    <Minus size={20} className="text-[#FF6B35]" />
                  ) : (
                    <Plus size={20} className="text-[#FF6B35]" />
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-base text-[rgba(0,0,0,0.5)] font-normal leading-normal pt-0">
                {typeof faq.answer === "string" ? (
                  <p>{faq.answer}</p>
                ) : (
                  faq.answer
                )}
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    );
  };

  return (
    <>
      <div className="bg-white pt-28 lg:pt-40 pb-20">
        <div className="4xl:max-w-[100rem] 4xl:mx-auto px-6 !mb-20">
          <div className="flex justify-center mb-[30px] mt-[15px] lg:mb-8 lg:mt-[-30px]">
            <div className="flex items-center gap-2 border border-border rounded-full px-4 py-2">
              <img src={QuestionMark} alt="Question Mark Icon" className="w-5 h-5" />
              <span className="font-normal leading-normal whitespace-nowrap text-[#FF6B35]">
                Help Center
              </span>
            </div>
          </div>

          {/* Main Heading */}
          <div ref={headingRef} className="text-center mb-8 animate-fade-rise">
            <h1 className="text-5xl xl:text-6xl mb-6 text-center font-semibold text-[#FF6B35] px-4 max-w-2xl mx-auto">
              <span className="text-[#FF6B35]">Knowledge Base</span>
              <br />
              <span className="text-black">& FAQs</span>
            </h1>
            <p className="text-lg text-black leading-[30px] max-w-xl mx-auto">
              Find answers to common questions about CreatorShield and learn how
              to protect your content.
            </p>
          </div>

          {/* Search Bar */}
          <div className="relative mb-12 max-w-[782px] mx-auto">
            <Input
              type="text"
              placeholder="Search for answers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="py-8 px-6 rounded-[10px] border-[#ddd] border focus-visible:ring-2 focus-visible:ring-[#FF6B35] text-[18px] text-black"
            />
            <Search className="absolute right-[22px] top-1/2 -translate-y-1/2 w-[30px] h-[30px] pointer-events-none text-gray-400" />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-[60px] top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            )}
          </div>

          {/* Knowledge Base Section */}
          <div className="mb-8 space-y-20 max-w-[850px] mx-auto">
            {/* Knowledge Base Section */}
            {filteredGeneral.length > 0 && (
              <div className="flex flex-col gap-[34px] no-animation">
                <div className="flex gap-[10px] items-center">
                  <BookOpen size={35} className="text-[#FF6B35]" />
                  <h2 className="text-2xl font-semibold text-black flex-1">
                    Knowledge Base
                  </h2>
                </div>
                {renderFAQSection(filteredGeneral, "general")}
              </div>
            )}

            {/* Before Joining Section */}
            {filteredBeforeJoining.length > 0 && (
              <div className="flex flex-col gap-[34px]">
                <div className="flex gap-[10px] items-center">
                  <Rocket size={35} className="text-[#FF6B35]" />
                  <h2 className="text-2xl font-semibold text-black flex-1">
                    Before Joining
                  </h2>
                </div>
                {renderFAQSection(filteredBeforeJoining, "before-joining")}
              </div>
            )}

            {/* Getting Paid Section */}
            {filteredGettingPaid.length > 0 && (
              <div className="flex flex-col gap-[34px]">
                <div className="flex gap-[10px] items-center">
                  <Wallet size={35} className="text-[#FF6B35]" />
                  <h2 className="text-2xl font-semibold text-black flex-1">
                    Getting Paid
                  </h2>
                </div>
                {renderFAQSection(filteredGettingPaid, "getting-paid")}
              </div>
            )}

            {/* General Settings Section */}
            {filteredGeneralSettings.length > 0 && (
              <div className="flex flex-col gap-[34px]">
                <div className="flex gap-[10px] items-center">
                  <Settings size={35} className="text-[#FF6B35]" />
                  <h2 className="text-2xl font-semibold text-black flex-1">
                    General Settings
                  </h2>
                </div>
                {renderFAQSection(filteredGeneralSettings, "general-settings")}
              </div>
            )}

            {/* No Results Message */}
            {filteredGeneral.length === 0 &&
              filteredBeforeJoining.length === 0 &&
              filteredGettingPaid.length === 0 &&
              filteredGeneralSettings.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg">
                    No results found for "{searchQuery}". Try a different search
                    term.
                  </p>
                </div>
              )}
          </div>
        </div>

        {/* Still Have Questions? CTA Section */}
        <FinalCTA />
      </div>
      <Footer />
    </>
  );
}
